import React, { useState, useEffect } from "react";
import { IonCol, IonInput, IonRow } from "@ionic/react";
import { Button } from "reactstrap";

interface QuantitySelectorProps {
    initialQuantity?: number;
    onChange?: (quantity: number) => void;
    min?: number;
    max?: number;
}

const QuantitySelector: React.FC<QuantitySelectorProps> = ({ initialQuantity, onChange, min = 0, max = 99 }) => {
    // Helper function to clamp value between min and max
    const clampValue = (value: number): number => {
        let clamped = Math.max(min, value);
        if (max !== undefined) {
            clamped = Math.min(max, clamped);
        }
        return clamped;
    };

    // Validate and clamp initial quantity
    const getValidatedInitialQuantity = (): number => {
        const initial = initialQuantity ?? min;
        return clampValue(initial);
    };

    const [quantity, setQuantity] = useState(getValidatedInitialQuantity());
    const [inputValue, setInputValue] = useState(getValidatedInitialQuantity().toString());

    // Validate initial quantity on mount
    useEffect(() => {
        const validated = getValidatedInitialQuantity();
        if (validated !== quantity) {
            setQuantity(validated);
            setInputValue(validated.toString());
            if (onChange) onChange(validated);
        }
    }, []); // Only run on mount

    // Sync internal state with prop changes
    useEffect(() => {
        if (initialQuantity !== undefined) {
            setQuantity(initialQuantity);
            setInputValue(initialQuantity.toString());
        }
    }, [initialQuantity]);

    const increment = () => {
        const newQuantity = quantity + 1;
        const clampedQuantity = clampValue(newQuantity);
        setQuantity(clampedQuantity);
        setInputValue(String(clampedQuantity));
        if (onChange) onChange(clampedQuantity);
    };

    const decrement = () => {
        const newQuantity = quantity - 1;
        const clampedQuantity = clampValue(newQuantity);
        setQuantity(clampedQuantity);
        setInputValue(String(clampedQuantity));
        if (onChange) onChange(clampedQuantity);
    };

    const handleInputChange = (event: any) => {
        const newValue = event.detail.value;
        // Filter out any non-numeric characters (only allow digits 0-9)
        // This prevents decimals and negative signs
        const numericValue = newValue.replace(/[^0-9]/g, "");
        setInputValue(numericValue); // Only update display value, don't commit yet
    };

    const commitQuantity = () => {
        // Handle empty input - default to 1 (but respect min/max bounds)
        if (inputValue === "" || inputValue.trim() === "") {
            const defaultQuantity = clampValue(1);
            setQuantity(defaultQuantity);
            setInputValue(String(defaultQuantity));
            if (onChange) onChange(defaultQuantity);
            return;
        }

        // Parse the input value (handles leading zeros automatically - "007" becomes 7)
        const parsedValue = parseInt(inputValue, 10);

        // If parsing fails, revert to current quantity
        if (isNaN(parsedValue)) {
            setInputValue(String(quantity));
            return;
        }

        // Clamp the value to min/max bounds
        // This handles: negative numbers (clamped to min), values above max (clamped to max)
        const clampedValue = clampValue(parsedValue);

        // Update state with clamped value
        setQuantity(clampedValue);
        setInputValue(String(clampedValue));
        if (onChange) onChange(clampedValue);
    };

    const handleInputBlur = () => {
        commitQuantity();
    };

    const handleKeyDown = (event: any) => {
        if (event.key === "Enter" || event.keyCode === 13) {
            event.preventDefault();
            commitQuantity();
            // Blur the input to close any keyboard on mobile
            const target = event.target || event.currentTarget;
            if (target) {
                target.blur();
            }
        }
        // Prevent non-numeric keys from being entered (except Enter, Backspace, Delete, Arrow keys, Tab)
        // Also explicitly prevent minus sign and decimal point
        const blockedKeys = ["-", ".", "+", "e", "E"]; // Prevent minus, decimal, plus, and scientific notation
        if (blockedKeys.includes(event.key)) {
            event.preventDefault();
            return;
        }

        const allowedKeys = ["Enter", "Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Tab", "Home", "End"];
        const isNumberKey = (event.key >= "0" && event.key <= "9") || (event.keyCode >= 48 && event.keyCode <= 57);
        const isNumpadKey = event.keyCode >= 96 && event.keyCode <= 105;
        const isAllowedKey = allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey;

        if (!isNumberKey && !isNumpadKey && !isAllowedKey) {
            event.preventDefault();
        }
    };

    return (
        <IonRow className="ion-align-items-center">
            <IonCol sizeXs="3" sizeSm="3" className="p-0 d-flex justify-content-center align-items-center">
                <Button size="xs" className="bg-danger text-white rounded-circle" style={{ width: "30px", height: "30px", padding: 0, textAlign: "center" }} onClick={decrement}>
                    -
                </Button>
            </IonCol>
            <IonCol sizeXs="5" sizeSm="4" className="p-0 mx-1 d-flex justify-content-center align-items-center">
                <IonInput
                    type="text"
                    inputMode="numeric"
                    className="m-0"
                    value={inputValue}
                    onIonChange={handleInputChange}
                    onIonBlur={handleInputBlur}
                    onKeyDown={handleKeyDown}
                    style={{ 
                        textAlign: "center", 
                        width: "30px",
                        height: "24px",
                        border: "1px solid #ccc",
                        borderRadius: "3px",
                        fontSize: "14px",
                        lineHeight: "1.2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                />
            </IonCol>
            <IonCol sizeXs="3" sizeSm="3" className="p-0 d-flex justify-content-center align-items-center">
                <Button size="xs" className="bg-success text-white rounded-circle" style={{ width: "30px", height: "30px", padding: 0, textAlign: "center" }} onClick={increment}>
                    +
                </Button>
            </IonCol>
        </IonRow>
    );
};

export default QuantitySelector;
